var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import * as Common from '_common';
import * as Components from '_components';
import { frames } from '_frameRegister';
import * as Styles from './PageFrames.module.scss';
export var FrameSize;
(function (FrameSize) {
    FrameSize[FrameSize["Minimized"] = 0] = "Minimized";
    FrameSize[FrameSize["Normal"] = 1] = "Normal";
    FrameSize[FrameSize["FullScreen"] = 2] = "FullScreen";
})(FrameSize || (FrameSize = {}));
export var FrameContext = React.createContext(undefined);
export function FrameProvider(props) {
    var _a = React.useState(function () {
        var storedFrames = localStorage.getItem('frames');
        if (storedFrames) {
            return JSON.parse(storedFrames);
        }
        return [
            {
                frame: {
                    id: 'default',
                    title: 'Default Frame',
                    component: { type: "Widgets_TimeSmall", properties: {} },
                },
                size: FrameSize.Normal,
            },
        ];
    }), frames = _a[0], setFrames = _a[1];
    React.useEffect(function () {
        localStorage.setItem('frames', JSON.stringify(frames));
    }, [frames]);
    return (_jsx(FrameContext.Provider, { value: [frames, setFrames], children: props.children }));
}
export function useFrameRegister() {
    var contextValue = React.useContext(FrameContext);
    if (!contextValue) {
        throw new Error("useFrameRegister must be used within a FrameProvider");
    }
    var frames = contextValue[0], setFrames = contextValue[1];
    function register(frame) {
        if (frames.find(function (f) { return f.frame.id === frame.id; })) {
            return;
        }
        var newFrameState = { frame: frame, size: FrameSize.Normal };
        setFrames(function (prevFrames) { return __spreadArray(__spreadArray([], prevFrames, true), [newFrameState], false); });
    }
    function remove(id) {
        setFrames(function (prevFrames) { return prevFrames.filter(function (frame) { return frame.frame.id !== id; }); });
    }
    function setSize(frame, size) {
        if (frame.size === size) {
            return frame;
        }
        return __assign(__assign({}, frame), { size: size });
    }
    function maximize(id) {
        setFrames(function (prevFrames) { return prevFrames.map(function (frame) { return frame.frame.id === id ? setSize(frame, FrameSize.FullScreen) : setSize(frame, FrameSize.Normal); }); });
    }
    function closeMaximized() {
        setFrames(function (prevFrames) { return prevFrames.map(function (frame) { return setSize(frame, FrameSize.Normal); }); });
    }
    function frame(id) {
        return frames.find(function (frame) { return frame.frame.id === id; });
    }
    function updateFrame(id, frame) {
        setFrames(function (prevFrames) { return prevFrames.map(function (f) { return f.frame.id === id ? frame : f; }); });
    }
    return { frames: frames, register: register, remove: remove, maximize: maximize, closeMaximized: closeMaximized, frame: frame, updateFrame: updateFrame };
}
export function NormalFrames() {
    var frameRegister = Common.useFrameRegister();
    var frames = frameRegister.frames.filter(function (frame) { return frame.size === FrameSize.Normal; });
    if (frames.length === 0) {
        return null;
    }
    return (_jsx("div", { className: Styles.main_frame_area, children: _jsx(Common.ErrorBoundary, { children: frames.map(function (frame) { return (_jsx(Frame, __assign({}, frame.frame), frame.frame.id)); }) }) }));
}
export function MinimizedFrames() {
    var frameRegister = Common.useFrameRegister();
    var overlay = Common.useOverlay();
    var frames = frameRegister.frames.filter(function (frame) { return frame.size === FrameSize.Minimized; });
    var minizedFrames = null;
    if (frames.length > 0) {
        minizedFrames =
            frames.map(function (frame) { return (_jsx(Frame, __assign({}, frame.frame), frame.frame.id)); });
    }
    return (_jsx(Components.WidgetSegment, { children: _jsx(Common.ErrorBoundary, { children: _jsxs(Components.BtnGroup, { children: [minizedFrames, _jsx(Components.Button, { onClick: function () { return overlay.setOverlay({ type: Common.OverlayType.FrameRegister }); }, children: "+" })] }) }) }));
}
export function Frame(props) {
    var _a, _b, _c, _d, _e;
    var frameRegister = Common.useFrameRegister();
    var state = frameRegister.frame(props.id);
    var _f = React.useState({}), positionState = _f[0], setPosition = _f[1];
    var _g = React.useState({ dragType: "none", initialX: 0, initialY: 0 }), resizeState = _g[0], setResizeState = _g[1];
    var ref = React.useRef(null);
    var componentToRender = __assign(__assign({}, props.component), { properties: __assign(__assign({}, (_a = props.component) === null || _a === void 0 ? void 0 : _a.properties), { frameSize: (_d = (_c = (_b = props.component) === null || _b === void 0 ? void 0 : _b.properties) === null || _c === void 0 ? void 0 : _c.frameSize) !== null && _d !== void 0 ? _d : FrameSize.Normal }) });
    if (!state) {
        return null;
    }
    if (state.size === FrameSize.Minimized) {
        return (_jsx(Components.Button, { onClick: function () { frameRegister.updateFrame(props.id, __assign(__assign({}, state), { size: FrameSize.Normal })); }, children: _jsx(Components.MLT, { text: (_e = props.title) !== null && _e !== void 0 ? _e : props.id }) }));
    }
    return (_jsx("div", { className: Styles.frame, style: positionState, ref: ref, children: _jsxs(Components.Widget, { children: [_jsxs(Components.WidgetComponent, { side: Components.WidgetComponentType.Top, type: Components.WidgetSideType.Big, children: [_jsx(Components.WidgetSegment, { children: _jsx("h1", { children: props.title ? _jsx(Components.MLT, { text: props.title }) : props.id }) }), _jsx(Components.WidgetSegment, { type: Components.WidgetSegmentType.Empty }), _jsx(Components.WidgetSegment, { children: _jsxs(Components.BtnGroup, { children: [_jsx(Components.Button, { onClick: function () {
                                            frameRegister.updateFrame(props.id, __assign(__assign({}, state), { size: FrameSize.Minimized }));
                                        }, children: "_" }), _jsx(Components.Button, { onClick: function () { return frameRegister.maximize(props.id); }, children: "[ ]" }), _jsx(Components.Button, { onClick: function () { return frameRegister.remove(props.id); }, children: "X" })] }) })] }), _jsx(Components.WidgetComponent, { side: Components.WidgetComponentType.Left, type: Components.WidgetSideType.Small }), _jsx(Components.WidgetComponent, { side: Components.WidgetComponentType.Right, type: Components.WidgetSideType.Small }), _jsx(Components.WidgetComponent, { side: Components.WidgetComponentType.Bottom, type: Components.WidgetSideType.Small }), _jsx(Components.WidgetComponent, { side: Components.WidgetComponentType.Content, children: _jsx("div", { className: Styles.frame_content, children: _jsx(Components.Content, { component: componentToRender }) }) })] }) }));
}
export function FrameSelection() {
    var frameRegister = Common.useFrameRegister();
    var overlayContext = Common.useOverlay();
    function showFrame(frame) {
        frameRegister.register(frame);
        overlayContext.setOverlay({
            type: Common.OverlayType.Null,
        });
    }
    return (_jsx(Components.BtnGroup, { children: frames.map(function (frame) { return (_jsx(Components.Button, { onClick: function () { showFrame(frame); }, children: _jsx(Components.MLT, { text: frame.title }) }, frame.id)); }) }));
}
